import { css } from '@emotion/react';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import {
  type ComponentProps, useCallback,
} from 'react';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { SliderWithValueLabelsComponent } from '~/_shared/components/slider/sliderWithValueLabels/sliderWithValueLabels.component';
import type { Theme } from '~/_shared/themes/theme.model';
import { useTranslation } from '~/_shared/utils/hooks';

const slicerContainer = css({
  cursor: 'default',
});

const helperText = css({
  fontSize: '.75em',
});

const helperTextSection = css({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'space-between',
});

const headerStyle = css({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'start',
});

const removeIconWrapperStyle = (theme: Theme) => css({
  alignItems: 'center',
  color: theme.iconColors.primary,
  display: 'inline-flex',
  fontSize: 16,
  height: 24,
  justifyContent: 'center',
  width: 24,
  '&:hover': {
    color: theme.iconColors.danger,
  },
});

const slider = css({
  paddingBottom: 0,
});

type WeightMetric = {
  groupId?: string;
  id: string;
  name: string;
  weight: number;
};

type DataWeightsItemProps = Omit<
  ComponentProps<typeof SliderWithValueLabelsComponent>,
  'min'|'max'|'onChange'|'noLabel'|'value'|'currentValueOnTheRight'|'className'|'valueSuffix'
> & {
  className?: string;
  maxWeight: number;
  metric: WeightMetric;
  minWeight: number;
  onChange: (newMetric: WeightMetric) => void;
  onRemove?: () => void;
};

export const DataWeightsItemComponent = ({
  metric, onChange, className, minWeight, maxWeight, onRemove, ...restProps
}: DataWeightsItemProps) => {
  const [t] = useTranslation();
  const onMetricChange = useCallback((newWeight: number) => {
    onChange({
      groupId: metric.groupId,
      id: metric.id,
      name: metric.name,
      weight: newWeight,
    });
  }, [metric.groupId, metric.id, metric.name, onChange]);

  return (
    <div className={className} css={slicerContainer}>
      <div css={headerStyle}>
        {onRemove && (
          <span css={removeIconWrapperStyle}>
            <FontAwesomeIcon
              title={t('Delete')}
              icon={faTimes}
              onClick={onRemove}
            />
          </span>
        )}
        <span>{metric.name}</span>
      </div>
      <SliderWithValueLabelsComponent
        {...restProps}
        css={slider}
        currentValueOnTheRight
        noLabel
        onChange={([weight]) => onMetricChange(weight)}
        value={[metric.weight]}
        min={minWeight}
        max={maxWeight}
        valueSuffix="%"
      />
      <span css={helperTextSection}>
        <span css={helperText}>{t('Less important')}</span>
        <span css={helperText}>{t('More important')}</span>
      </span>
    </div>
  );
};
